import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TaskHistoryItem, TaskTelemetryDetail } from '../types';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

// Formatters — kept local to avoid pulling in a date/number lib
// for what amounts to "$0.42" / "12.3k". Tight number formatting
// matters: the row already crams 7 cells, so token counts have to
// be 4 chars max ("228k") or they wrap and break the grid.
function formatCost(usd: number | null | undefined): string {
  if (usd == null) return '—';
  if (usd === 0) return '$0';
  if (usd < 0.01) return '<$0.01';
  return `$${usd.toFixed(2)}`;
}
function formatTokens(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}

export const TaskHistory: React.FC = () => {
  const [history, setHistory] = useState<TaskHistoryItem[]>([]);
  const [filter, setFilter] = useState<'all' | 'completed' | 'failed'>('all');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, TaskTelemetryDetail | 'loading' | 'missing'>>({});

  const reload = useCallback(() => {
    fetch(`${API}/api/tasks/history`)
      .then((res) => res.json())
      .then((data) => { if (Array.isArray(data)) setHistory(data); })
      .catch(() => {});
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const removeTask = async (id: string) => {
    if (!window.confirm('Удалить эту задачу из истории? Логи и телеметрия удалятся вместе с ней.')) return;
    setDeleting(id);
    try {
      const r = await fetch(`${API}/api/tasks/${id}`, { method: 'DELETE' });
      if (r.ok) {
        setHistory((prev) => prev.filter((t) => t.id !== id));
      } else {
        const data = await r.json().catch(() => ({}));
        alert(`Не удалось удалить: ${data.message || data.error || `HTTP ${r.status}`}`);
      }
    } catch (err: any) {
      alert(`Сетевая ошибка: ${err.message || 'unknown'}`);
    } finally {
      setDeleting(null);
    }
  };

  // Lazy-fetch the heavy detail (tool list + files) only when the
  // user actually expands a row. The list response already carries
  // the summary numbers, so the row collapses to a no-op fetch if
  // we're just toggling visibility.
  const toggleExpand = async (id: string) => {
    if (expanded === id) {
      setExpanded(null);
      return;
    }
    setExpanded(id);
    if (!details[id]) {
      setDetails((prev) => ({ ...prev, [id]: 'loading' }));
      try {
        const r = await fetch(`${API}/api/tasks/${id}/telemetry`);
        if (r.status === 404) {
          setDetails((prev) => ({ ...prev, [id]: 'missing' }));
        } else if (r.ok) {
          const data: TaskTelemetryDetail = await r.json();
          setDetails((prev) => ({ ...prev, [id]: data }));
        } else {
          setDetails((prev) => ({ ...prev, [id]: 'missing' }));
        }
      } catch {
        setDetails((prev) => ({ ...prev, [id]: 'missing' }));
      }
    }
  };

  const filtered = filter === 'all' ? history : history.filter((t) => t.status === filter);

  return (
    <div className="task-history">
      <div className="history-header">
        <h3>История задач</h3>
        <div className="history-filters">
          {(['all', 'completed', 'failed'] as const).map((f) => (
            <button
              key={f}
              className={`filter-btn ${filter === f ? 'filter-active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'Все' : f === 'completed' ? 'Готово' : 'Ошибки'}
            </button>
          ))}
        </div>
      </div>

      <div className="history-table">
        <div className="history-table-header">
          <span>Задача</span>
          <span>Модуль</span>
          <span>Работник</span>
          <span>Статус</span>
          <span>Время · Cost</span>
          <span>Коммит</span>
          <span></span>
        </div>
        {filtered.map((item, i) => {
          const isOpen = expanded === item.id;
          // Compact summary line: duration · cost · in→out tokens · turns
          // Each piece falls back to a dash when the underlying field
          // is null so the layout doesn't shift between rows with /
          // without telemetry.
          const cost = formatCost(item.costUsd);
          const inT = formatTokens(item.inputTokens);
          const outT = formatTokens(item.outputTokens);
          const turns = item.turnCount != null ? `${item.turnCount}t` : '';
          const summaryParts = [item.duration || '-', cost];
          if (item.inputTokens != null) summaryParts.push(`${inT}→${outT}`);
          if (turns) summaryParts.push(turns);
          const summary = summaryParts.join(' · ');

          return (
            <React.Fragment key={item.id}>
              <motion.div
                className="history-row history-row-clickable"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => toggleExpand(item.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleExpand(item.id); }}
                aria-expanded={isOpen}
                title="Открыть детали"
              >
                <span className="history-title">{item.title}</span>
                <span className="history-module">{item.module}</span>
                <span className="history-worker">{item.worker}</span>
                <span className={`history-status ${item.status === 'completed' ? 'status-ok' : 'status-fail'}`}>
                  {item.status === 'completed' ? 'OK' : 'FAIL'}
                </span>
                <span className="history-duration history-summary" title={item.model ? `model: ${item.model}` : undefined}>
                  {summary}
                </span>
                <span className="history-commit">
                  {item.commitHash ? item.commitHash.slice(0, 8) : '-'}
                  {item.mrUrl && (
                    <a
                      href={item.mrUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="history-mr-link"
                      onClick={(e) => e.stopPropagation()}
                      title="Открыть Merge Request в GitLab"
                    >
                      MR↗
                    </a>
                  )}
                </span>
                <button
                  className="history-delete"
                  onClick={(e) => { e.stopPropagation(); removeTask(item.id); }}
                  disabled={deleting === item.id}
                  aria-label="Удалить задачу"
                  title="Удалить задачу из истории"
                >
                  {deleting === item.id ? '…' : '×'}
                </button>
              </motion.div>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    key="detail"
                    className="history-detail"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <TaskDetail item={item} state={details[item.id]} />
                  </motion.div>
                )}
              </AnimatePresence>
            </React.Fragment>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state">Нет задач для отображения</div>
      )}
    </div>
  );
};

// Sub-component — renders the expanded panel under a clicked row.
// Three states: loading (just opened, fetch in flight), missing
// (404 or error — pre-telemetry / spawn-fail task), populated.
const TaskDetail: React.FC<{
  item: TaskHistoryItem;
  state: TaskTelemetryDetail | 'loading' | 'missing' | undefined;
}> = ({ item, state }) => {
  if (state === undefined || state === 'loading') {
    return <div className="history-detail-empty">Загружаю детали…</div>;
  }
  if (state === 'missing') {
    return (
      <div className="history-detail-empty">
        Детальной телеметрии нет (задача завершилась до того, как Claude
        успел отчитаться по токенам).
      </div>
    );
  }

  // De-dup tool names: when Claude reads the same file 30 times we
  // want "Read ×30" not 30 rows. Sort by frequency desc so the
  // pattern that drove most spend is at the top.
  const toolCounts = new Map<string, number>();
  for (const u of state.toolUses || []) {
    toolCounts.set(u.name, (toolCounts.get(u.name) || 0) + 1);
  }
  const tools = Array.from(toolCounts.entries()).sort((a, b) => b[1] - a[1]);

  const files = state.filesTouched || [];
  const commits = state.commitHashes || [];

  // Cache stats — when this is high, we're paying ~$1.50/1M for
  // those tokens instead of $15/1M. Useful sanity check that
  // prompt caching is actually engaging on this task.
  const cacheRead = state.cacheReadTokens || 0;
  const cacheWrite = state.cacheCreationTokens || 0;
  const cachePct = state.inputTokens > 0
    ? Math.round((cacheRead / state.inputTokens) * 100)
    : 0;

  return (
    <div className="history-detail-grid">
      <div className="history-detail-block">
        <div className="history-detail-title">Стоимость</div>
        <div className="history-detail-value">{formatCost(state.costUsd)}</div>
        <div className="history-detail-sub">
          {formatTokens(state.inputTokens)} input · {formatTokens(state.outputTokens)} output
          {state.turnCount > 0 && ` · ${state.turnCount} turn${state.turnCount === 1 ? '' : 's'}`}
        </div>
        {state.inputTokens > 0 && (
          <div className="history-detail-sub" title="Tokens served from prompt cache (~10× cheaper than uncached input)">
            кэш: {formatTokens(cacheRead)} ({cachePct}%){cacheWrite > 0 && ` · запись: ${formatTokens(cacheWrite)}`}
          </div>
        )}
        {state.model && <div className="history-detail-sub history-detail-mono">{state.model}</div>}
      </div>

      <div className="history-detail-block">
        <div className="history-detail-title">Инструменты</div>
        {tools.length === 0
          ? <div className="history-detail-sub">—</div>
          : (
            <div className="history-detail-list">
              {tools.slice(0, 10).map(([name, count]) => (
                <div key={name} className="history-detail-row">
                  <span className="history-detail-mono">{name}</span>
                  <span className="history-detail-count">×{count}</span>
                </div>
              ))}
              {tools.length > 10 && (
                <div className="history-detail-sub">…ещё {tools.length - 10}</div>
              )}
            </div>
          )}
      </div>

      <div className="history-detail-block">
        <div className="history-detail-title">Файлы ({files.length})</div>
        {files.length === 0
          ? <div className="history-detail-sub">—</div>
          : (
            <div className="history-detail-list">
              {files.slice(0, 12).map((f) => (
                <div key={f} className="history-detail-mono history-detail-file">{f}</div>
              ))}
              {files.length > 12 && (
                <div className="history-detail-sub">…ещё {files.length - 12}</div>
              )}
            </div>
          )}
      </div>

      <div className="history-detail-block">
        <div className="history-detail-title">Коммиты ({commits.length})</div>
        {commits.length === 0
          ? <div className="history-detail-sub">—</div>
          : (
            <div className="history-detail-list">
              {commits.map((c) => (
                <div key={c} className="history-detail-mono">{c.slice(0, 12)}</div>
              ))}
            </div>
          )}
      </div>

      {/* Branch + MR — full-width row at the bottom of the grid.
          Lets the operator jump straight to the GitLab branch / MR
          without first finding the project. Renders even when only
          one of the two is set (legacy tasks have neither). */}
      {(item.branchName || item.mrUrl) && (
        <div className="history-detail-block history-detail-block-wide">
          <div className="history-detail-title">Ветка / MR</div>
          <div className="history-detail-sub history-detail-mono">
            {item.branchName && <span>{item.branchName}</span>}
            {item.branchName && item.mrUrl && <span style={{ margin: '0 8px' }}>·</span>}
            {item.mrUrl && (
              <a href={item.mrUrl} target="_blank" rel="noopener noreferrer" className="history-mr-link">
                открыть MR ↗
              </a>
            )}
          </div>
        </div>
      )}

      {item.startedAt && (
        <div className="history-detail-block history-detail-block-wide">
          <div className="history-detail-title">Время</div>
          <div className="history-detail-sub">
            старт: {new Date(item.startedAt).toLocaleString('ru-RU')}
            {item.completedAt && ` · финиш: ${new Date(item.completedAt).toLocaleString('ru-RU')}`}
          </div>
        </div>
      )}
    </div>
  );
};
